import { type MedplumClient, OperationOutcomeError } from "@medplum/core";
import type { LoadPracticeAppointmentTypeAvailabilityResult } from "@/data/scheduling/load-practice-appointment-type-availability";
import { loadPracticeAppointmentTypeAvailability } from "@/data/scheduling/load-practice-appointment-type-availability";
import { shouldEnforceEffectiveAvailability } from "@/data/scheduling/should-enforce-effective-availability";
import { type CalManagedUser, createCalAdminClient } from "@/lib/cal/admin";
import {
  type AvailabilityFormValues,
  buildAvailabilityComparisonKey,
  buildAvailabilityFormValues,
  buildAvailabilityScheduleRequest,
  doAvailabilitySchedulesDiffer,
} from "@/lib/cal/availability-mapper";
import { CalClient, type CalEventType, type CalSchedule, createCalClient } from "@/lib/cal/client";
import { parseCalTeamId } from "@/lib/cal/parse-cal-team-id";
import {
  isCustomRoomAvailabilitySchedule,
  ROOM_AVAILABILITY_SCHEDULE_NAME,
} from "@/lib/cal/read-availability";
import {
  doesAvailabilityHaveAnyRanges,
  intersectAvailabilitySchedules,
} from "@/lib/calendar/effective-availability";
import { CalUserUnableToRefreshError } from "@/lib/calendar/effective-availability-sync-errors";
import type { EffectiveAvailabilitySyncReason } from "@/lib/calendar/effective-availability-sync-reasons";
import { NotFoundError } from "@/lib/errors";
import { isCalNotFoundError, isCalRefreshableAuthError } from "@/lib/errors/cal";
import { createLogger } from "@/lib/logger";
import { Practice } from "@/models/practice/Practice";
import { PracticeAppointmentType } from "@/models/practice/PracticeAppointmentType";
import { Room } from "@/models/practice/Room";
import { type CalCredentials, patchCalCredentials } from "@/models/provider/CalCredentials";
import { Provider } from "@/models/provider/Provider";

const logger = createLogger("sync-practice-appointment-type-effective-availability");
const EFFECTIVE_SCHEDULE_NAME_PREFIX = "Vibrant Effective Availability";
const DERIVED_AVAILABILITY_METADATA_WRITE_MAX_ATTEMPTS = 3;

export interface SyncPracticeAppointmentTypeEffectiveAvailabilityInput {
  practiceId: string;
  appointmentTypeId?: string;
  providerId?: string;
  roomId?: string;
  reason?: EffectiveAvailabilitySyncReason;
}

export interface SyncPracticeAppointmentTypeEffectiveAvailabilityResult {
  scannedAppointmentTypes: number;
  scannedHosts: number;
  updatedCount: number;
  unchangedCount: number;
  skippedCount: number;
  emptyAvailabilityCount: number;
}

interface HostContext {
  kind: "provider" | "room";
  hostId: string;
  hostName: string;
  calUserId: number;
  defaultScheduleId?: number;
  hasExplicitAvailability: boolean;
  timeZone: string;
  availability: AvailabilityFormValues;
  calClient: CalClient;
}

interface ProjectionResult {
  status: "updated" | "unchanged" | "skipped";
  appointmentType: PracticeAppointmentType;
}

type ManagedUserIdentity = CalManagedUser["user"];

interface LoadHostContextArgs {
  medplum: MedplumClient;
  calAdmin: ReturnType<typeof createCalAdminClient>;
  practice: Practice;
  kind: HostContext["kind"];
  hostId: string;
  forceRefresh?: boolean;
}

const DEFAULT_HOST_TIMEZONE = "America/Los_Angeles";

/**
 * Picks the timezone used to compute intersection of host and appointment
 * availability. We deliberately prefer the schedule's own timeZone and the
 * practice timezone over the managed user's timeZone because:
 *
 *   - The schedule's timeZone is the source of truth for the working hours
 *     that were actually authored in the UI.
 *   - The managed user's timeZone can drift for reasons outside our control
 *     (Cal dashboard edits, atoms auto-writes, manual CLI patches). When it
 *     drifts, using it here causes the intersection logic to rotate ranges
 *     across midnight, which produced invalid "24:00" endTimes and blocked
 *     all bookings for the provider (see Jeffrey Graham incident, Apr 2026).
 *   - The practice timezone is our server-side ground truth for the clinic.
 *
 * Logs a warning when we detect drift so we can self-heal it upstream.
 */
function resolveHostTimeZone({
  scheduleTimeZone,
  practiceTimeZone,
  managedUserTimeZone,
  hostType,
  hostId,
  practiceId,
}: {
  scheduleTimeZone: string | undefined;
  practiceTimeZone: string | undefined;
  managedUserTimeZone: string | undefined;
  hostType: "provider" | "room";
  hostId: string;
  practiceId: string;
}): string {
  const resolved =
    scheduleTimeZone?.trim() ||
    practiceTimeZone?.trim() ||
    managedUserTimeZone?.trim() ||
    DEFAULT_HOST_TIMEZONE;

  if (
    managedUserTimeZone &&
    practiceTimeZone &&
    managedUserTimeZone !== practiceTimeZone &&
    managedUserTimeZone !== resolved
  ) {
    logger.warn("Managed user timeZone differs from practice/schedule timeZone", {
      practiceId,
      hostType,
      hostId,
      scheduleTimeZone,
      practiceTimeZone,
      managedUserTimeZone,
      resolvedTimeZone: resolved,
    });
  }

  return resolved;
}

/**
 * Patches the Cal managed user's timeZone back to the timezone we actually use
 * to compute effective availability. This is our self-healing for the drift
 * class of bug: the managed user can end up in e.g. Asia/Manila (via manual
 * CLI, Cal dashboard edit, or buggy atoms state) while the schedule and
 * practice are in America/New_York, which then rotates ranges across midnight
 * and produces invalid schedule payloads.
 *
 * Safe to call on every sync: when timezones already match, it's a no-op. The
 * patch failing should never block the sync — the caller still has a resolved
 * timezone we can use immediately, and the drift warning will fire again next
 * run.
 */
async function healManagedUserTimeZoneDrift({
  calAdmin,
  managedUser,
  desiredTimeZone,
  hostType,
  hostId,
  practiceId,
}: {
  calAdmin: ReturnType<typeof createCalAdminClient>;
  managedUser: ManagedUserIdentity;
  desiredTimeZone: string;
  hostType: "provider" | "room";
  hostId: string;
  practiceId: string;
}): Promise<void> {
  const currentTimeZone = managedUser.timeZone?.trim();
  if (!desiredTimeZone || currentTimeZone === desiredTimeZone) {
    return;
  }

  try {
    await calAdmin.updateManagedUser(managedUser.id, { timeZone: desiredTimeZone });
    logger.info("Healed Cal managed user timeZone drift", {
      practiceId,
      hostType,
      hostId,
      calUserId: managedUser.id,
      previousTimeZone: currentTimeZone ?? null,
      desiredTimeZone,
    });
  } catch (error) {
    logger.warn("Failed to heal Cal managed user timeZone drift", {
      practiceId,
      hostType,
      hostId,
      calUserId: managedUser.id,
      previousTimeZone: currentTimeZone ?? null,
      desiredTimeZone,
      error,
    });
  }
}

function getProviderCalUserIdSafely(provider: Provider): number | null {
  try {
    return provider.calUserId;
  } catch {
    return null;
  }
}

function getRoomCalUserIdSafely(room: Room): number | null {
  try {
    return room.calUserId;
  } catch {
    return null;
  }
}

async function forceRefreshProviderCalCredentials({
  medplum,
  calAdmin,
  provider,
  calCredentials,
  managedUser,
}: {
  medplum: MedplumClient;
  calAdmin: ReturnType<typeof createCalAdminClient>;
  provider: Provider;
  calCredentials: CalCredentials;
  managedUser: ManagedUserIdentity;
}): Promise<CalCredentials> {
  let refreshedTokens: Awaited<ReturnType<typeof calAdmin.refreshManagedUserTokens>>;
  try {
    refreshedTokens = await calAdmin.refreshManagedUserTokens(managedUser.id);
  } catch (_error) {
    throw new CalUserUnableToRefreshError();
  }

  const providerCalUserId = getProviderCalUserIdSafely(provider);
  if (
    providerCalUserId !== managedUser.id ||
    provider.calUsername !== managedUser.username ||
    provider.calManagedEmail !== managedUser.email
  ) {
    await Provider.patchWithQuery(medplum, provider.id, {
      calUserId: managedUser.id,
      calUsername: managedUser.username,
      calManagedEmail: managedUser.email,
    });
  }

  return await patchCalCredentials(medplum, {
    id: calCredentials.id,
    props: {
      accessToken: refreshedTokens.accessToken,
      accessTokenExpiresAt: refreshedTokens.accessTokenExpiresAt,
      refreshToken: refreshedTokens.refreshToken,
    },
    options: {
      expectedCurrentAccessToken: calCredentials.accessToken,
    },
  });
}

async function forceRefreshRoomCalCredentials({
  medplum,
  calAdmin,
  room,
  calCredentials,
  managedUser,
}: {
  medplum: MedplumClient;
  calAdmin: ReturnType<typeof createCalAdminClient>;
  room: Room;
  calCredentials: CalCredentials;
  managedUser: ManagedUserIdentity;
}): Promise<CalCredentials> {
  let refreshedTokens: Awaited<ReturnType<typeof calAdmin.refreshManagedUserTokens>>;
  try {
    refreshedTokens = await calAdmin.refreshManagedUserTokens(managedUser.id);
  } catch (_error) {
    throw new CalUserUnableToRefreshError();
  }

  const roomCalUserId = getRoomCalUserIdSafely(room);
  if (
    roomCalUserId !== managedUser.id ||
    room.calUsername !== managedUser.username ||
    room.calManagedEmail !== managedUser.email
  ) {
    await Room.update(medplum, room.id, {
      calUserId: managedUser.id,
      calUsername: managedUser.username,
      calManagedEmail: managedUser.email,
    });
  }

  return await patchCalCredentials(medplum, {
    id: calCredentials.id,
    props: {
      accessToken: refreshedTokens.accessToken,
      accessTokenExpiresAt: refreshedTokens.accessTokenExpiresAt,
      refreshToken: refreshedTokens.refreshToken,
    },
    options: {
      expectedCurrentAccessToken: calCredentials.accessToken,
    },
  });
}

async function loadHostContextWithForcedRefreshRetry({
  medplum,
  calAdmin,
  practice,
  kind,
  hostId,
}: Omit<LoadHostContextArgs, "forceRefresh">): Promise<HostContext | undefined> {
  try {
    return await loadHostContext({
      medplum,
      calAdmin,
      practice,
      kind,
      hostId,
    });
  } catch (error) {
    if (!isCalRefreshableAuthError(error)) {
      throw error;
    }

    logger.warn(
      "Host context load failed with auth error during effective availability sync, forcing Cal refresh",
      {
        practiceId: practice.id,
        hostType: kind,
        hostId,
        error,
      },
    );

    try {
      return await loadHostContext({
        medplum,
        calAdmin,
        practice,
        kind,
        hostId,
        forceRefresh: true,
      });
    } catch (retryError) {
      logger.error("Host context load still failed after forced Cal refresh", {
        practiceId: practice.id,
        hostType: kind,
        hostId,
        error,
        retryError,
      });
      throw retryError;
    }
  }
}

async function retryHostCalOperationAfterForcedRefresh<T>({
  medplum,
  calAdmin,
  practice,
  hostContext,
  initialError,
  operationName,
  logContext,
  onBeforeRetry,
  run,
}: {
  medplum: MedplumClient;
  calAdmin: ReturnType<typeof createCalAdminClient>;
  practice: Practice;
  hostContext: HostContext;
  initialError: unknown;
  operationName: string;
  logContext?: Record<string, unknown>;
  onBeforeRetry?: () => void;
  run: (hostContext: HostContext) => Promise<T>;
}): Promise<{ result: T; hostContext: HostContext }> {
  logger.warn("Host Cal operation failed during effective availability sync, forcing Cal refresh", {
    practiceId: practice.id,
    hostType: hostContext.kind,
    hostId: hostContext.hostId,
    operationName,
    ...logContext,
    error: initialError,
  });

  onBeforeRetry?.();

  const refreshedHostContext = await loadHostContext({
    medplum,
    calAdmin,
    practice,
    kind: hostContext.kind,
    hostId: hostContext.hostId,
    forceRefresh: true,
  });
  if (!refreshedHostContext) {
    throw new CalUserUnableToRefreshError();
  }

  return {
    result: await run(refreshedHostContext),
    hostContext: refreshedHostContext,
  };
}

async function runHostCalOperationWithForcedRefreshRetry<T>({
  medplum,
  calAdmin,
  practice,
  hostContext,
  operationName,
  logContext,
  onBeforeRetry,
  run,
}: {
  medplum: MedplumClient;
  calAdmin: ReturnType<typeof createCalAdminClient>;
  practice: Practice;
  hostContext: HostContext;
  operationName: string;
  logContext?: Record<string, unknown>;
  onBeforeRetry?: () => void;
  run: (hostContext: HostContext) => Promise<T>;
}): Promise<{ result: T; hostContext: HostContext }> {
  try {
    return {
      result: await run(hostContext),
      hostContext,
    };
  } catch (error) {
    if (!isCalRefreshableAuthError(error)) {
      throw error;
    }

    return await retryHostCalOperationAfterForcedRefresh({
      medplum,
      calAdmin,
      practice,
      hostContext,
      initialError: error,
      operationName,
      logContext,
      onBeforeRetry,
      run,
    });
  }
}

/**
 * Recomputes child-event schedules so Booker always reads appointmentType ∩ host availability.
 */
export async function syncPracticeAppointmentTypeEffectiveAvailability(
  medplum: MedplumClient,
  input: SyncPracticeAppointmentTypeEffectiveAvailabilityInput,
): Promise<SyncPracticeAppointmentTypeEffectiveAvailabilityResult> {
  const practice = await Practice.getById(medplum, input.practiceId);
  const teamId = parseCalTeamId({
    calTeamId: practice.calTeamId,
    practiceId: practice.id,
  });
  const appointmentTypes = await resolveAppointmentTypesToSync({
    medplum,
    input,
  });
  if (appointmentTypes.length === 0) {
    return {
      scannedAppointmentTypes: 0,
      scannedHosts: 0,
      updatedCount: 0,
      unchangedCount: 0,
      skippedCount: 0,
      emptyAvailabilityCount: 0,
    };
  }

  const calTeamClient = CalClient.forTeam();
  const teamEventTypesResponse = await calTeamClient.getTeamEventTypes({ teamId });
  if (teamEventTypesResponse.status !== "success") {
    throw new Error(`Failed to load team event types for practice ${practice.id}`);
  }

  const teamEventTypesById = new Map(
    teamEventTypesResponse.data.map((eventType) => [eventType.id, eventType]),
  );
  const calAdmin = createCalAdminClient();

  let scannedHosts = 0;
  let updatedCount = 0;
  let unchangedCount = 0;
  let skippedCount = 0;
  let emptyAvailabilityCount = 0;
  let failedHostCount = 0;
  let firstHostError: unknown;

  for (const appointmentType of appointmentTypes) {
    let appointmentTypeForSync = appointmentType;
    const parentEventTypeId = appointmentType.calTeamEventTypeId;
    const parentEventType =
      typeof parentEventTypeId === "number" ? teamEventTypesById.get(parentEventTypeId) : undefined;
    if (!parentEventType) {
      logger.warn("Skipping effective availability sync because parent event type is missing", {
        practiceId: practice.id,
        appointmentTypeId: appointmentType.id,
        parentEventTypeId,
      });
      skippedCount += 1;
      continue;
    }

    const hostTargets = getAppointmentTypeHostTargets({
      appointmentType,
      providerIdFilter: input.providerId,
      roomIdFilter: input.roomId,
    });
    if (hostTargets.length === 0) {
      skippedCount += 1;
      continue;
    }

    for (const hostTarget of hostTargets) {
      scannedHosts += 1;

      // Isolate one host's failure. The job retries the whole practice, so an unhandled throw here
      // leaves every host later in the loop with a stale projection on every attempt.
      try {
        let hostContext = await loadHostContextWithForcedRefreshRetry({
          medplum,
          calAdmin,
          practice,
          kind: hostTarget.kind,
          hostId: hostTarget.hostId,
        });
        if (!hostContext) {
          skippedCount += 1;
          continue;
        }

        const childEventTypeId = appointmentTypeForSync.getChildEventTypeIdForCalUser(
          hostContext.calUserId,
        );
        if (typeof childEventTypeId !== "number") {
          logger.warn(
            "Skipping effective availability sync because child event mapping is missing",
            {
              practiceId: practice.id,
              appointmentTypeId: appointmentTypeForSync.id,
              calUserId: hostContext.calUserId,
              hostType: hostContext.kind,
              hostId: hostContext.hostId,
            },
          );
          skippedCount += 1;
          continue;
        }

        const appointmentAvailabilityLoad = await runHostCalOperationWithForcedRefreshRetry({
          medplum,
          calAdmin,
          practice,
          hostContext,
          operationName: "load-appointment-type-availability",
          logContext: {
            appointmentTypeId: appointmentTypeForSync.id,
            parentEventTypeId: parentEventType.id,
          },
          run: async (nextHostContext) =>
            await loadAppointmentTypeAvailability({
              appointmentType: appointmentTypeForSync,
              hostContext: nextHostContext,
              parentEventType,
              practice,
            }),
        });
        hostContext = appointmentAvailabilityLoad.hostContext;
        const appointmentAvailabilityResult = appointmentAvailabilityLoad.result;
        if (!appointmentAvailabilityResult.calendarReadable) {
          logger.warn(
            "Appointment type schedule is unreadable; clearing any existing effective-availability projection",
            {
              practiceId: practice.id,
              appointmentTypeId: appointmentTypeForSync.id,
              parentEventTypeId: parentEventType.id,
              hostType: hostContext.kind,
              hostId: hostContext.hostId,
            },
          );
          const clearResult = await clearEffectiveAvailabilityProjectionFromChildEvent({
            appointmentType: appointmentTypeForSync,
            hostContext,
            childEventTypeId,
            medplum,
            calAdmin,
            practice,
          });
          appointmentTypeForSync = clearResult.appointmentType;

          if (clearResult.status === "updated") {
            updatedCount += 1;
          } else {
            unchangedCount += 1;
          }
          continue;
        }

        if (
          !shouldEnforceEffectiveAvailability({
            appointmentType: appointmentTypeForSync,
            appointmentAvailabilityResult,
            hasExplicitHostAvailability: hostContext.hasExplicitAvailability,
          })
        ) {
          const clearResult = await clearEffectiveAvailabilityProjectionFromChildEvent({
            appointmentType: appointmentTypeForSync,
            hostContext,
            childEventTypeId,
            medplum,
            calAdmin,
            practice,
          });
          appointmentTypeForSync = clearResult.appointmentType;

          if (clearResult.status === "updated") {
            updatedCount += 1;
          } else {
            unchangedCount += 1;
          }
          continue;
        }

        const effectiveAvailability = intersectAvailabilitySchedules({
          appointmentAvailability: appointmentAvailabilityResult.availability,
          hostAvailability: hostContext.availability,
          targetTimeZone: hostContext.timeZone,
          name: buildEffectiveAvailabilityName({
            appointmentType: appointmentTypeForSync,
            hostContext,
          }),
        });
        if (!doesAvailabilityHaveAnyRanges({ availability: effectiveAvailability })) {
          emptyAvailabilityCount += 1;
        }

        const projectionResult = await projectEffectiveAvailabilityToChildEvent({
          appointmentType: appointmentTypeForSync,
          effectiveAvailability,
          hostContext,
          childEventTypeId,
          medplum,
          parentEventType,
          calAdmin,
          practice,
        });
        appointmentTypeForSync = projectionResult.appointmentType;

        if (projectionResult.status === "updated") {
          updatedCount += 1;
        } else if (projectionResult.status === "unchanged") {
          unchangedCount += 1;
        } else {
          skippedCount += 1;
        }
      } catch (error) {
        failedHostCount += 1;
        firstHostError ??= error;
        logger.error("Effective availability sync failed for one host; continuing with the rest", {
          practiceId: practice.id,
          appointmentTypeId: appointmentTypeForSync.id,
          hostType: hostTarget.kind,
          hostId: hostTarget.hostId,
          error,
        });
      }
    }
  }

  logger.info("Synced practice appointment type effective availability", {
    practiceId: practice.id,
    appointmentTypeId: input.appointmentTypeId,
    providerId: input.providerId,
    roomId: input.roomId,
    reason: input.reason,
    scannedAppointmentTypes: appointmentTypes.length,
    scannedHosts,
    updatedCount,
    unchangedCount,
    skippedCount,
    emptyAvailabilityCount,
    failedHostCount,
  });

  if (failedHostCount > 0) {
    // Every healthy host now carries a fresh projection. Surface the first failure so the caller
    // and the job's retries still see the real Cal or Medplum error.
    throw firstHostError instanceof Error
      ? firstHostError
      : new Error(`Effective availability sync failed for ${failedHostCount} host(s)`);
  }

  return {
    scannedAppointmentTypes: appointmentTypes.length,
    scannedHosts,
    updatedCount,
    unchangedCount,
    skippedCount,
    emptyAvailabilityCount,
  };
}

/**
 * Limits the appointment-type scope for targeted provider/room drift repair.
 */
async function resolveAppointmentTypesToSync({
  medplum,
  input,
}: {
  medplum: MedplumClient;
  input: SyncPracticeAppointmentTypeEffectiveAvailabilityInput;
}): Promise<PracticeAppointmentType[]> {
  if (input.appointmentTypeId) {
    const appointmentType = await PracticeAppointmentType.getById(medplum, input.appointmentTypeId);
    return appointmentType.practiceId === input.practiceId ? [appointmentType] : [];
  }

  if (input.providerId) {
    return PracticeAppointmentType.searchAllByProvider(medplum, input.providerId, input.practiceId);
  }

  if (input.roomId) {
    return PracticeAppointmentType.searchAllByRoom(medplum, input.roomId, input.practiceId);
  }

  return PracticeAppointmentType.searchAllByPractice(medplum, input.practiceId);
}

/**
 * Resolves the selected host IDs the product can actually book against.
 */
function getAppointmentTypeHostTargets({
  appointmentType,
  providerIdFilter,
  roomIdFilter,
}: {
  appointmentType: PracticeAppointmentType;
  providerIdFilter?: string;
  roomIdFilter?: string;
}): Array<{ kind: "provider" | "room"; hostId: string }> {
  if (appointmentType.type === "room") {
    if (providerIdFilter) {
      return [];
    }

    const roomIds = roomIdFilter
      ? appointmentType.roomIds.filter((roomId) => roomId === roomIdFilter)
      : appointmentType.roomIds;
    return roomIds.map((roomId) => ({ kind: "room", hostId: roomId }));
  }

  if (roomIdFilter) {
    return [];
  }

  const providerIds = providerIdFilter
    ? appointmentType.providerIds.filter((providerId) => providerId === providerIdFilter)
    : appointmentType.providerIds;
  return providerIds.map((providerId) => ({ kind: "provider", hostId: providerId }));
}

/**
 * Loads a provider or room's default schedule and the Cal client needed to mutate its child events.
 */
async function loadHostContext({
  medplum,
  calAdmin,
  practice,
  kind,
  hostId,
  forceRefresh = false,
}: LoadHostContextArgs): Promise<HostContext | undefined> {
  if (kind === "provider") {
    let providerCalendar: Awaited<ReturnType<typeof Provider.ensureCalendar>>;
    try {
      providerCalendar = await Provider.ensureCalendar(medplum, calAdmin, hostId);
    } catch (error) {
      if (!(error instanceof NotFoundError)) {
        throw error;
      }

      // Appointment types can retain a stale actor after provider deletion; other hosts still sync.
      logger.warn("Skipping missing provider host during effective availability sync", {
        practiceId: practice.id,
        providerId: hostId,
      });
      return undefined;
    }

    const { provider, calCredentials } = providerCalendar;
    const managedUser = await Provider.getManagedUserForProvider(calAdmin, provider);
    const providerCalUserId = getProviderCalUserIdSafely(provider);

    if (!calCredentials || !managedUser || (!forceRefresh && providerCalUserId == null)) {
      logger.warn("Provider host is missing Cal linkage for effective availability sync", {
        practiceId: practice.id,
        providerId: hostId,
        hasCalCredentials: Boolean(calCredentials),
        hasManagedUser: Boolean(managedUser),
        calUserId: providerCalUserId,
        forceRefresh,
      });
      return undefined;
    }

    const resolvedCalCredentials = forceRefresh
      ? await forceRefreshProviderCalCredentials({
          medplum,
          calAdmin,
          provider,
          calCredentials,
          managedUser,
        })
      : calCredentials;
    const calUserId = forceRefresh ? managedUser.id : providerCalUserId;
    if (calUserId == null) {
      return undefined;
    }

    const calClient = await createCalClient(resolvedCalCredentials);
    const schedule = await loadScheduleIfPresent({
      calClient,
      scheduleId: managedUser.defaultScheduleId ?? undefined,
      logContext: {
        practiceId: practice.id,
        providerId: provider.id,
        hostType: "provider",
      },
    });

    const resolvedTimeZone = resolveHostTimeZone({
      scheduleTimeZone: schedule?.timeZone,
      practiceTimeZone: practice.timezone,
      managedUserTimeZone: managedUser.timeZone,
      hostType: "provider",
      hostId: provider.id,
      practiceId: practice.id,
    });

    await healManagedUserTimeZoneDrift({
      calAdmin,
      managedUser,
      desiredTimeZone: resolvedTimeZone,
      hostType: "provider",
      hostId: provider.id,
      practiceId: practice.id,
    });

    return {
      kind,
      hostId: provider.id,
      hostName: provider.name ?? provider.id,
      calUserId,
      defaultScheduleId: managedUser.defaultScheduleId ?? undefined,
      hasExplicitAvailability: true,
      timeZone: resolvedTimeZone,
      availability: buildAvailabilityFormValues({
        schedule,
        timeZoneFallback: resolvedTimeZone,
        isDefault: true,
      }),
      calClient,
    };
  }

  const { room, calCredentials } = await Room.ensureCalendar(medplum, calAdmin, hostId);
  const managedUser = await Room.getManagedUserForRoom(calAdmin, room);
  const roomCalUserId = getRoomCalUserIdSafely(room);

  if (!calCredentials || !managedUser || (!forceRefresh && roomCalUserId == null)) {
    logger.warn("Room host is missing Cal linkage for effective availability sync", {
      practiceId: practice.id,
      roomId: hostId,
      hasCalCredentials: Boolean(calCredentials),
      hasManagedUser: Boolean(managedUser),
      calUserId: roomCalUserId,
      forceRefresh,
    });
    return undefined;
  }

  const resolvedCalCredentials = forceRefresh
    ? await forceRefreshRoomCalCredentials({
        medplum,
        calAdmin,
        room,
        calCredentials,
        managedUser,
      })
    : calCredentials;
  const calUserId = forceRefresh ? managedUser.id : roomCalUserId;
  if (calUserId == null) {
    return undefined;
  }

  const calClient = await createCalClient(resolvedCalCredentials);
  const schedule = await loadScheduleIfPresent({
    calClient,
    scheduleId: managedUser.defaultScheduleId ?? undefined,
    logContext: {
      practiceId: practice.id,
      roomId: room.id,
      hostType: "room",
    },
  });
  const hasCustomSchedule = isCustomRoomAvailabilitySchedule({
    scheduleName: schedule?.name,
  });

  const resolvedTimeZone = resolveHostTimeZone({
    scheduleTimeZone: schedule?.timeZone,
    practiceTimeZone: practice.timezone,
    managedUserTimeZone: managedUser.timeZone,
    hostType: "room",
    hostId: room.id,
    practiceId: practice.id,
  });

  await healManagedUserTimeZoneDrift({
    calAdmin,
    managedUser,
    desiredTimeZone: resolvedTimeZone,
    hostType: "room",
    hostId: room.id,
    practiceId: practice.id,
  });

  return {
    kind,
    hostId: room.id,
    hostName: room.name,
    calUserId,
    defaultScheduleId: managedUser.defaultScheduleId ?? undefined,
    hasExplicitAvailability: hasCustomSchedule,
    timeZone: resolvedTimeZone,
    availability: buildAvailabilityFormValues({
      schedule: hasCustomSchedule ? schedule : null,
      timeZoneFallback: resolvedTimeZone,
      nameOverride: ROOM_AVAILABILITY_SCHEDULE_NAME,
      isDefault: true,
    }),
    calClient,
  };
}

/**
 * Loads the appointment-type schedule using the selected host's Cal credentials.
 */
async function loadAppointmentTypeAvailability({
  appointmentType,
  hostContext,
  parentEventType,
  practice,
}: {
  appointmentType: PracticeAppointmentType;
  hostContext: HostContext;
  parentEventType: CalEventType;
  practice: Practice;
}): Promise<LoadPracticeAppointmentTypeAvailabilityResult> {
  return await loadPracticeAppointmentTypeAvailability({
    appointmentType,
    practiceTimeZone: practice.timezone,
    hostTimeZone: hostContext.timeZone,
    logContext: {
      practiceId: practice.id,
      appointmentTypeId: appointmentType.id,
      parentEventTypeId: parentEventType.id,
      hostType: hostContext.kind,
      hostId: hostContext.hostId,
    },
  });
}

/**
 * Clears child-event schedule projection when availability should not be enforced yet.
 */
async function clearEffectiveAvailabilityProjectionFromChildEvent({
  appointmentType,
  hostContext,
  childEventTypeId,
  medplum,
  calAdmin,
  practice,
}: {
  appointmentType: PracticeAppointmentType;
  hostContext: HostContext;
  childEventTypeId: number;
  medplum: MedplumClient;
  calAdmin: ReturnType<typeof createCalAdminClient>;
  practice: Practice;
}): Promise<ProjectionResult> {
  let activeHostContext = hostContext;
  const childEventTypeResult = await runHostCalOperationWithForcedRefreshRetry({
    medplum,
    calAdmin,
    practice,
    hostContext: activeHostContext,
    operationName: "get-child-event-type-for-clear",
    logContext: {
      appointmentTypeId: appointmentType.id,
      childEventTypeId,
    },
    run: async (nextHostContext) => await nextHostContext.calClient.getEventType(childEventTypeId),
  });
  activeHostContext = childEventTypeResult.hostContext;
  const childEventType = childEventTypeResult.result;
  const persistedDerivedSchedule = appointmentType.getDerivedAvailabilityScheduleForCalUser(
    activeHostContext.calUserId,
  );
  const doesHaveAttachedSchedule = childEventType.scheduleId != null;

  if (doesHaveAttachedSchedule) {
    const clearEventTypeResult = await runHostCalOperationWithForcedRefreshRetry({
      medplum,
      calAdmin,
      practice,
      hostContext: activeHostContext,
      operationName: "clear-child-event-schedule",
      logContext: {
        appointmentTypeId: appointmentType.id,
        childEventTypeId: childEventType.id,
      },
      run: async (nextHostContext) =>
        await nextHostContext.calClient.updateEventType({
          eventTypeId: childEventType.id,
          request: { scheduleId: null },
        }),
    });
    activeHostContext = clearEventTypeResult.hostContext;
  }

  if (!persistedDerivedSchedule && !doesHaveAttachedSchedule) {
    return {
      status: "unchanged",
      appointmentType,
    };
  }

  const updatedAppointmentType = await clearDerivedAvailabilityScheduleMetadata({
    appointmentType,
    calUserId: activeHostContext.calUserId,
    medplum,
  });

  return {
    status: "updated",
    appointmentType: updatedAppointmentType,
  };
}

/**
 * Applies the derived overlap schedule to the exact child event Booker reads.
 */
async function projectEffectiveAvailabilityToChildEvent({
  appointmentType,
  effectiveAvailability,
  hostContext,
  childEventTypeId,
  medplum,
  parentEventType,
  calAdmin,
  practice,
}: {
  appointmentType: PracticeAppointmentType;
  effectiveAvailability: AvailabilityFormValues;
  hostContext: HostContext;
  childEventTypeId: number;
  medplum: MedplumClient;
  parentEventType: CalEventType;
  calAdmin: ReturnType<typeof createCalAdminClient>;
  practice: Practice;
}): Promise<ProjectionResult> {
  let activeHostContext = hostContext;
  const childEventTypeResult = await runHostCalOperationWithForcedRefreshRetry({
    medplum,
    calAdmin,
    practice,
    hostContext: activeHostContext,
    operationName: "get-child-event-type",
    logContext: {
      appointmentTypeId: appointmentType.id,
      childEventTypeId,
    },
    run: async (nextHostContext) => await nextHostContext.calClient.getEventType(childEventTypeId),
  });
  activeHostContext = childEventTypeResult.hostContext;
  const childEventType = childEventTypeResult.result;
  const currentChildScheduleId = childEventType.scheduleId ?? undefined;
  const expectedFingerprint = buildAvailabilityComparisonKey({
    formState: effectiveAvailability,
  });
  const persistedDerivedSchedule = appointmentType.getDerivedAvailabilityScheduleForCalUser(
    activeHostContext.calUserId,
  );
  const existingDerivedScheduleId =
    persistedDerivedSchedule?.scheduleId ??
    (typeof currentChildScheduleId === "number" &&
    currentChildScheduleId !== parentEventType.scheduleId &&
    currentChildScheduleId !== activeHostContext.defaultScheduleId
      ? currentChildScheduleId
      : undefined);
  const isExistingDerivedSchedule = typeof existingDerivedScheduleId === "number";
  const hasMatchingPersistedDerivedScheduleFingerprint =
    isExistingDerivedSchedule &&
    currentChildScheduleId === existingDerivedScheduleId &&
    persistedDerivedSchedule?.fingerprint === expectedFingerprint;

  if (isExistingDerivedSchedule && existingDerivedScheduleId) {
    const currentChildScheduleLoad = await runHostCalOperationWithForcedRefreshRetry({
      medplum,
      calAdmin,
      practice,
      hostContext: activeHostContext,
      operationName: "load-current-child-schedule",
      logContext: {
        appointmentTypeId: appointmentType.id,
        childEventTypeId,
      },
      run: async (nextHostContext) =>
        await loadScheduleIfPresent({
          calClient: nextHostContext.calClient,
          scheduleId: existingDerivedScheduleId,
          logContext: {
            appointmentTypeId: appointmentType.id,
            childEventTypeId,
            hostType: nextHostContext.kind,
            hostId: nextHostContext.hostId,
          },
        }),
    });
    activeHostContext = currentChildScheduleLoad.hostContext;
    const currentChildSchedule = currentChildScheduleLoad.result;
    if (currentChildSchedule) {
      const currentChildAvailability = buildAvailabilityFormValues({
        schedule: currentChildSchedule,
        timeZoneFallback: activeHostContext.timeZone,
        nameOverride: childEventType.title,
        isDefault: false,
      });

      if (
        !doAvailabilitySchedulesDiffer({
          left: currentChildAvailability,
          right: effectiveAvailability,
        })
      ) {
        if (hasMatchingPersistedDerivedScheduleFingerprint) {
          return {
            status: "unchanged",
            appointmentType,
          };
        }

        const unchangedAppointmentType = await persistDerivedAvailabilityScheduleMetadata({
          appointmentType,
          calUserId: activeHostContext.calUserId,
          medplum,
          scheduleId: existingDerivedScheduleId,
          fingerprint: expectedFingerprint,
        });

        if (currentChildScheduleId !== existingDerivedScheduleId) {
          const updateEventTypeResult = await runHostCalOperationWithForcedRefreshRetry({
            medplum,
            calAdmin,
            practice,
            hostContext: activeHostContext,
            operationName: "repoint-child-event-derived-schedule",
            logContext: {
              appointmentTypeId: appointmentType.id,
              childEventTypeId: childEventType.id,
              scheduleId: existingDerivedScheduleId,
            },
            run: async (nextHostContext) =>
              await nextHostContext.calClient.updateEventType({
                eventTypeId: childEventType.id,
                request: {
                  scheduleId: existingDerivedScheduleId,
                },
              }),
          });
          activeHostContext = updateEventTypeResult.hostContext;
        }

        return {
          status: currentChildScheduleId === existingDerivedScheduleId ? "unchanged" : "updated",
          appointmentType: unchangedAppointmentType,
        };
      }
    }
  }

  let nextScheduleId = existingDerivedScheduleId;
  if (isExistingDerivedSchedule && existingDerivedScheduleId) {
    try {
      await activeHostContext.calClient.updateSchedule(
        existingDerivedScheduleId,
        buildAvailabilityScheduleRequest({
          formState: effectiveAvailability,
          timeZoneFallback: activeHostContext.timeZone,
          nameOverride: buildEffectiveAvailabilityName({
            appointmentType,
            hostContext: activeHostContext,
          }),
          isDefault: false,
          allowEmptyAvailability: true,
        }),
      );
    } catch (error) {
      if (isCalNotFoundError(error)) {
        nextScheduleId = undefined;
      } else {
        if (!isCalRefreshableAuthError(error)) {
          throw error;
        }

        const updateScheduleResult = await retryHostCalOperationAfterForcedRefresh({
          medplum,
          calAdmin,
          practice,
          hostContext: activeHostContext,
          initialError: error,
          operationName: "update-existing-derived-schedule",
          logContext: {
            appointmentTypeId: appointmentType.id,
            childEventTypeId,
            scheduleId: existingDerivedScheduleId,
          },
          run: async (nextHostContext) =>
            await nextHostContext.calClient.updateSchedule(
              existingDerivedScheduleId,
              buildAvailabilityScheduleRequest({
                formState: effectiveAvailability,
                timeZoneFallback: nextHostContext.timeZone,
                nameOverride: buildEffectiveAvailabilityName({
                  appointmentType,
                  hostContext: nextHostContext,
                }),
                isDefault: false,
                allowEmptyAvailability: true,
              }),
            ),
        });
        activeHostContext = updateScheduleResult.hostContext;
        nextScheduleId = existingDerivedScheduleId;
      }
    }
  } else {
    nextScheduleId = undefined;
  }

  if (!nextScheduleId) {
    const latestAppointmentType = await PracticeAppointmentType.getById(
      medplum,
      appointmentType.id,
    );
    const latestDerivedSchedule = latestAppointmentType.getDerivedAvailabilityScheduleForCalUser(
      activeHostContext.calUserId,
    );
    if (
      latestDerivedSchedule?.scheduleId &&
      latestDerivedSchedule.scheduleId !== parentEventType.scheduleId &&
      latestDerivedSchedule.scheduleId !== activeHostContext.defaultScheduleId
    ) {
      try {
        await activeHostContext.calClient.updateSchedule(
          latestDerivedSchedule.scheduleId,
          buildAvailabilityScheduleRequest({
            formState: effectiveAvailability,
            timeZoneFallback: activeHostContext.timeZone,
            nameOverride: buildEffectiveAvailabilityName({
              appointmentType,
              hostContext: activeHostContext,
            }),
            isDefault: false,
            allowEmptyAvailability: true,
          }),
        );
        nextScheduleId = latestDerivedSchedule.scheduleId;
        appointmentType = latestAppointmentType;
      } catch (error) {
        if (!isCalNotFoundError(error)) {
          if (!isCalRefreshableAuthError(error)) {
            throw error;
          }

          const latestScheduleUpdateResult = await retryHostCalOperationAfterForcedRefresh({
            medplum,
            calAdmin,
            practice,
            hostContext: activeHostContext,
            initialError: error,
            operationName: "update-latest-derived-schedule",
            logContext: {
              appointmentTypeId: appointmentType.id,
              childEventTypeId,
              scheduleId: latestDerivedSchedule.scheduleId,
            },
            run: async (nextHostContext) =>
              await nextHostContext.calClient.updateSchedule(
                latestDerivedSchedule.scheduleId,
                buildAvailabilityScheduleRequest({
                  formState: effectiveAvailability,
                  timeZoneFallback: nextHostContext.timeZone,
                  nameOverride: buildEffectiveAvailabilityName({
                    appointmentType,
                    hostContext: nextHostContext,
                  }),
                  isDefault: false,
                  allowEmptyAvailability: true,
                }),
              ),
          });
          activeHostContext = latestScheduleUpdateResult.hostContext;
          nextScheduleId = latestDerivedSchedule.scheduleId;
          appointmentType = latestAppointmentType;
        }
      }
    }
  }

  if (!nextScheduleId) {
    const createScheduleResult = await runHostCalOperationWithForcedRefreshRetry({
      medplum,
      calAdmin,
      practice,
      hostContext: activeHostContext,
      operationName: "create-derived-schedule",
      logContext: {
        appointmentTypeId: appointmentType.id,
        childEventTypeId,
      },
      run: async (nextHostContext) =>
        await nextHostContext.calClient.createSchedule(
          buildAvailabilityScheduleRequest({
            formState: effectiveAvailability,
            timeZoneFallback: nextHostContext.timeZone,
            nameOverride: buildEffectiveAvailabilityName({
              appointmentType,
              hostContext: nextHostContext,
            }),
            isDefault: false,
            allowEmptyAvailability: true,
          }),
        ),
    });
    activeHostContext = createScheduleResult.hostContext;
    const createdSchedule = createScheduleResult.result;
    nextScheduleId = createdSchedule.id;
  }

  if (childEventType.scheduleId !== nextScheduleId) {
    const updateEventTypeResult = await runHostCalOperationWithForcedRefreshRetry({
      medplum,
      calAdmin,
      practice,
      hostContext: activeHostContext,
      operationName: "attach-derived-schedule-to-child-event",
      logContext: {
        appointmentTypeId: appointmentType.id,
        childEventTypeId: childEventType.id,
        scheduleId: nextScheduleId,
      },
      run: async (nextHostContext) =>
        await nextHostContext.calClient.updateEventType({
          eventTypeId: childEventType.id,
          request: {
            scheduleId: nextScheduleId,
          },
        }),
    });
    activeHostContext = updateEventTypeResult.hostContext;
  }

  const updatedAppointmentType = await persistDerivedAvailabilityScheduleMetadata({
    appointmentType,
    calUserId: activeHostContext.calUserId,
    medplum,
    scheduleId: nextScheduleId,
    fingerprint: expectedFingerprint,
  });

  return {
    status: "updated",
    appointmentType: updatedAppointmentType,
  };
}

/**
 * Persists the stable derived schedule id/fingerprint for one host so later syncs can reuse it.
 */
async function persistDerivedAvailabilityScheduleMetadata({
  appointmentType,
  calUserId,
  medplum,
  scheduleId,
  fingerprint,
}: {
  appointmentType: PracticeAppointmentType;
  calUserId: number;
  medplum: MedplumClient;
  scheduleId: number;
  fingerprint?: string;
}): Promise<PracticeAppointmentType> {
  let lastError: unknown;

  for (let attempt = 1; attempt <= DERIVED_AVAILABILITY_METADATA_WRITE_MAX_ATTEMPTS; attempt += 1) {
    const latestAppointmentType = await PracticeAppointmentType.getById(
      medplum,
      appointmentType.id,
    );
    const currentMapping =
      latestAppointmentType.getDerivedAvailabilityScheduleForCalUser(calUserId);
    if (currentMapping?.scheduleId === scheduleId && currentMapping.fingerprint === fingerprint) {
      return latestAppointmentType;
    }

    const nextDerivedAvailabilitySchedules = buildMergedDerivedAvailabilitySchedules({
      derivedAvailabilitySchedules: latestAppointmentType.derivedAvailabilitySchedules,
      calUserId,
      scheduleId,
      fingerprint,
    });
    const latestResource = latestAppointmentType.serialize();
    const preservedExtensions =
      latestResource.extension?.filter(
        (extension) =>
          extension.url !== PracticeAppointmentType.Extension.DerivedAvailabilitySchedules,
      ) ?? [];

    try {
      const updatedAppointmentType = await medplum.updateResource({
        ...latestResource,
        extension: [
          ...preservedExtensions,
          {
            url: PracticeAppointmentType.Extension.DerivedAvailabilitySchedules,
            valueString: JSON.stringify(nextDerivedAvailabilitySchedules),
          },
        ],
      });

      return new PracticeAppointmentType(updatedAppointmentType);
    } catch (error) {
      if (!isVersionConflictError({ error })) {
        throw error;
      }

      lastError = error;
      const isFinalAttempt = attempt >= DERIVED_AVAILABILITY_METADATA_WRITE_MAX_ATTEMPTS;
      if (isFinalAttempt) {
        break;
      }

      logger.warn("Retrying derived availability schedule metadata merge after version conflict", {
        appointmentTypeId: appointmentType.id,
        calUserId,
        scheduleId,
        attempt,
      });
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error("Failed to persist derived availability schedule metadata");
}

/**
 * Removes one host's derived schedule metadata after projection is intentionally disabled.
 */
async function clearDerivedAvailabilityScheduleMetadata({
  appointmentType,
  calUserId,
  medplum,
}: {
  appointmentType: PracticeAppointmentType;
  calUserId: number;
  medplum: MedplumClient;
}): Promise<PracticeAppointmentType> {
  let lastError: unknown;

  for (let attempt = 1; attempt <= DERIVED_AVAILABILITY_METADATA_WRITE_MAX_ATTEMPTS; attempt += 1) {
    const latestAppointmentType = await PracticeAppointmentType.getById(
      medplum,
      appointmentType.id,
    );
    if (!latestAppointmentType.getDerivedAvailabilityScheduleForCalUser(calUserId)) {
      return latestAppointmentType;
    }

    const nextDerivedAvailabilitySchedules =
      latestAppointmentType.derivedAvailabilitySchedules.filter(
        (mapping) => mapping.calUserId !== calUserId,
      );
    const latestResource = latestAppointmentType.serialize();
    const preservedExtensions =
      latestResource.extension?.filter(
        (extension) =>
          extension.url !== PracticeAppointmentType.Extension.DerivedAvailabilitySchedules,
      ) ?? [];

    try {
      const updatedAppointmentType = await medplum.updateResource({
        ...latestResource,
        extension:
          nextDerivedAvailabilitySchedules.length > 0
            ? [
                ...preservedExtensions,
                {
                  url: PracticeAppointmentType.Extension.DerivedAvailabilitySchedules,
                  valueString: JSON.stringify(nextDerivedAvailabilitySchedules),
                },
              ]
            : preservedExtensions,
      });

      return new PracticeAppointmentType(updatedAppointmentType);
    } catch (error) {
      if (!isVersionConflictError({ error })) {
        throw error;
      }

      lastError = error;
      const isFinalAttempt = attempt >= DERIVED_AVAILABILITY_METADATA_WRITE_MAX_ATTEMPTS;
      if (isFinalAttempt) {
        break;
      }

      logger.warn("Retrying derived availability schedule metadata clear after version conflict", {
        appointmentTypeId: appointmentType.id,
        calUserId,
        attempt,
      });
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error("Failed to clear derived availability schedule metadata");
}

/**
 * Merges one host's derived schedule metadata into the latest appointment-type snapshot.
 */
function buildMergedDerivedAvailabilitySchedules({
  derivedAvailabilitySchedules,
  calUserId,
  scheduleId,
  fingerprint,
}: {
  derivedAvailabilitySchedules: Array<{
    calUserId: number;
    scheduleId: number;
    fingerprint?: string;
  }>;
  calUserId: number;
  scheduleId: number;
  fingerprint?: string;
}): Array<{
  calUserId: number;
  scheduleId: number;
  fingerprint?: string;
}> {
  return [
    ...derivedAvailabilitySchedules.filter((mapping) => mapping.calUserId !== calUserId),
    {
      calUserId,
      scheduleId,
      fingerprint,
    },
  ].sort((left, right) => left.calUserId - right.calUserId);
}

function getErrorStatus({ error }: { error: unknown }): number | undefined {
  if (!error || typeof error !== "object") {
    return undefined;
  }

  const status = (error as { status?: unknown }).status;
  return typeof status === "number" ? status : undefined;
}

function isVersionConflictMessage({ message }: { message: string | undefined }): boolean {
  if (!message) {
    return false;
  }

  const normalizedMessage = message.toLowerCase();
  return (
    normalizedMessage.includes("conflict") ||
    normalizedMessage.includes("precondition failed") ||
    normalizedMessage.includes("if-match") ||
    normalizedMessage.includes("versionid")
  );
}

function isVersionConflictError({ error }: { error: unknown }): boolean {
  if (error instanceof OperationOutcomeError) {
    const issueCode = error.outcome?.issue?.[0]?.code;
    if (issueCode === "conflict") {
      return true;
    }

    const diagnostics = error.outcome?.issue?.[0]?.diagnostics?.toLowerCase() ?? "";
    if (
      isVersionConflictMessage({ message: diagnostics }) ||
      isVersionConflictMessage({ message: error.message })
    ) {
      return true;
    }

    const status = getErrorStatus({ error });
    return status === 409 || status === 412;
  }

  const status = getErrorStatus({ error });
  if (status === 409 || status === 412) {
    return true;
  }

  if (error instanceof Error) {
    return isVersionConflictMessage({ message: error.message });
  }

  return false;
}

/**
 * Loads a Cal schedule when the schedule ID is present.
 */
async function loadScheduleIfPresent({
  calClient,
  scheduleId,
  logContext,
}: {
  calClient: CalClient;
  scheduleId?: number;
  logContext: Record<string, unknown>;
}): Promise<CalSchedule | undefined> {
  if (!scheduleId) {
    return undefined;
  }

  try {
    return await calClient.getSchedule(scheduleId);
  } catch (error) {
    if (!isCalNotFoundError(error)) {
      throw error;
    }

    logger.warn("Cal schedule missing during effective availability sync", {
      ...logContext,
      scheduleId,
    });
    return undefined;
  }
}

/**
 * Keeps derived schedules easy to spot in Cal during rollout/debugging.
 */
function buildEffectiveAvailabilityName({
  appointmentType,
  hostContext,
}: {
  appointmentType: PracticeAppointmentType;
  hostContext: HostContext;
}): string {
  return `${EFFECTIVE_SCHEDULE_NAME_PREFIX}: ${appointmentType.title} / ${hostContext.hostName}`;
}
